import { useState, useMemo } from "react"
import { Sparkles, ChevronUp, ChevronDown, Loader2 } from "lucide-react"
import { trpc } from "../../../lib/trpc"
import { ChatGPTModelType, ClaudeModelType, GeminiModelType } from "@aix/apiutils/src/llms/enums"

// Badges for recommended models
const MODEL_BADGES: Record<string, string> = {
  [ChatGPTModelType.GPT_5_2]: "Most Capable",
  [ClaudeModelType.SONNET_45]: "Best for Coding",
  [GeminiModelType.GEMINI_3_1_PRO_PREVIEW]: "Best For UI",
}

interface ModelSelectorProps {
  selectedModelId: string
  onSelectModel: (modelId: string) => void
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  viewMode?: "flat" | "byFamily" // "flat" = sorted list, "byFamily" = grouped by provider
  compact?: boolean // Smaller trigger button for tight spaces
}

const ModelSelector = ({
  selectedModelId,
  onSelectModel,
  isOpen,
  onOpenChange,
  viewMode = "flat",
  compact = false,
}: ModelSelectorProps) => {
  const [showAll, setShowAll] = useState(false)

  // Fetch models from backend
  const { data: modelsData, isLoading } = trpc.aura.getModels.useQuery(undefined, {
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  })

  // Process models for display
  const { recommendedModels, additionalModels, selectedModel, modelsByFamily } = useMemo(() => {
    if (!modelsData) {
      return {
        recommendedModels: [],
        additionalModels: [],
        selectedModel: { id: selectedModelId, name: selectedModelId },
        modelsByFamily: {},
      }
    }

    const recommended = modelsData.recommended || []
    const allModels = modelsData.sorted || []

    // Recommended models (featured)
    const recModels = allModels
      .filter((m) => (recommended as string[]).includes(m.id))
      .map((m) => ({ ...m, badge: MODEL_BADGES[m.id] }))

    // Additional models (non-featured)
    const addModels = allModels.filter((m) => !(recommended as string[]).includes(m.id))

    // Find selected model
    const selected = allModels.find((m) => m.id === selectedModelId) ||
      recModels[0] || { id: selectedModelId, name: selectedModelId }

    return {
      recommendedModels: recModels,
      additionalModels: addModels,
      selectedModel: selected,
      modelsByFamily: modelsData.byFamily || {},
    }
  }, [modelsData, selectedModelId])

  const handleSelect = (modelId: string) => {
    onSelectModel(modelId)
    onOpenChange(false)
  }

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => onOpenChange(!isOpen)}
        className={`flex items-center gap-2 bg-gray-800 rounded-lg font-medium hover:bg-gray-700 transition ${
          compact ? "px-2.5 py-1.5 text-xs" : "px-4 py-2 text-sm"
        }`}
      >
        <Sparkles className={compact ? "w-3.5 h-3.5 text-purple-400" : "w-4 h-4 text-purple-400"} />
        {isLoading ? "..." : selectedModel.name}
        {isOpen ? (
          <ChevronUp className={compact ? "w-3 h-3 text-gray-400" : "w-3 h-3 text-gray-400"} />
        ) : (
          <ChevronDown className={compact ? "w-3 h-3 text-gray-400" : "w-3 h-3 text-gray-400"} />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute bottom-full left-0 mb-2 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl py-2 min-w-[280px] z-50">
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : viewMode === "byFamily" ? (
            // Grouped by provider family
            <>
              {Object.entries(modelsByFamily).map(([provider, models]) => (
                <div key={provider}>
                  <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-800">
                    {(models as any)[0]?.providerName || provider}
                  </div>
                  {(models as any[]).map((model) => (
                    <button
                      key={model.id}
                      onClick={() => handleSelect(model.id)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-800 transition ${
                        selectedModelId === model.id ? "bg-gray-800" : ""
                      }`}
                    >
                      <Sparkles className="w-4 h-4 text-purple-400 flex-shrink-0" />
                      <span className="font-medium text-white">{model.name}</span>
                      {MODEL_BADGES[model.id] && (
                        <span className="ml-auto px-2 py-0.5 bg-gray-700 text-gray-300 text-xs rounded-md">
                          {MODEL_BADGES[model.id]}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              ))}
            </>
          ) : (
            // Flat list with recommended at top
            <>
              {/* Recommended Models */}
              {recommendedModels.map((model) => (
                <button
                  key={model.id}
                  onClick={() => handleSelect(model.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-800 transition ${
                    selectedModelId === model.id ? "bg-gray-800" : ""
                  }`}
                >
                  <Sparkles className="w-4 h-4 text-purple-400 flex-shrink-0" />
                  <span className="font-medium text-white">{model.name}</span>
                  {model.badge && (
                    <span className="ml-auto px-2 py-0.5 bg-gray-700 text-gray-300 text-xs rounded-md">
                      {model.badge}
                    </span>
                  )}
                </button>
              ))}

              {/* Show More/Less Toggle */}
              {additionalModels.length > 0 && (
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 transition border-t border-gray-800 mt-1"
                >
                  {showAll ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Show less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Show more ({additionalModels.length})
                    </>
                  )}
                </button>
              )}

              {/* Additional Models (shown when expanded) */}
              {showAll && (
                <div className="border-t border-gray-800">
                  {additionalModels.map((model) => (
                    <button
                      key={model.id}
                      onClick={() => handleSelect(model.id)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-800 transition ${
                        selectedModelId === model.id ? "bg-gray-800" : ""
                      }`}
                    >
                      <Sparkles className="w-4 h-4 text-purple-400 flex-shrink-0" />
                      <span className="font-medium text-white">{model.name}</span>
                      <span className="ml-auto text-xs text-gray-500">{model.providerName}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default ModelSelector

// Export the default model ID for initial state
export const DEFAULT_MODEL_ID = GeminiModelType.GEMINI_3_1_PRO_PREVIEW

// Helper to get model name from ID (for display when data not loaded yet)
export const getModelDisplayName = (modelId: string): string => {
  // Fallback display names for common models
  const FALLBACK_NAMES: Record<string, string> = {
    [ChatGPTModelType.GPT_5_2]: "GPT-5.2",
    [ClaudeModelType.SONNET_45]: "Claude 4.5 Sonnet",
    [GeminiModelType.GEMINI_3_1_PRO_PREVIEW]: "Gemini 3.1 Pro",
    [ChatGPTModelType.GPT_4_1]: "GPT-4.1",
    [ChatGPTModelType.GPT_4o]: "GPT-4o",
  }
  return FALLBACK_NAMES[modelId] || modelId
}
